import { WidgetConfig, WidgetData } from '../services/types';

interface MiniWidgetProps {
  config: WidgetConfig;
  data: WidgetData;
  isActive?: boolean;
  onClick: () => void;
  onStatClick?: (filterValue: string) => void;
}

// Map stat labels to filter values
const statLabelToFilter: Record<string, string> = {
  'New': 'new',
  'Progress': 'in-progress',
  'Wait Us': 'waiting-us',
  'Wait Cust': 'waiting-customer',
};

export default function MiniWidget({ config, data, isActive, onClick, onStatClick }: MiniWidgetProps) {
  const statusColors: Record<string, string> = {
    healthy: 'var(--accent-green)',
    warning: 'var(--accent-yellow)',
    critical: 'var(--accent-red)',
    loading: 'var(--text-secondary)',
    error: 'var(--accent-red)',
  };

  // Map stat type to color
  const statTypeColors: Record<string, string> = {
    critical: 'var(--accent-red)',
    orange: 'var(--accent-orange)',
    warning: 'var(--accent-yellow)',
    info: 'var(--accent-blue)',
    success: 'var(--accent-green)',
  };

  // Check if we have stats to show
  const hasStats = data.stats && data.stats.length > 0 && data.status !== 'loading' && data.status !== 'error';
  const showNotConfigured = !hasStats && data.status !== 'loading';

  // Handle click on the widget header (not stats)
  const handleWidgetClick = (e: React.MouseEvent) => {
    // Only navigate if clicking the header area, not the stats
    const target = e.target as HTMLElement;
    if (!target.closest('.mini-widget-stats')) {
      onClick();
    }
  };

  return (
    <div
      className={`mini-widget ${isActive ? 'active' : ''} ${hasStats ? 'has-stats' : ''}`}
      onClick={handleWidgetClick}
      title={config.name}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    >
      <div className="mini-widget-header">
        <span className="mini-widget-icon">{config.icon}</span>
        <span className="mini-widget-name">{config.name}</span>
        <span
          className="mini-widget-status"
          style={{ backgroundColor: statusColors[data.status] || statusColors.loading }}
        />
      </div>
      {hasStats && (
        <div className="mini-widget-stats">
          {data.stats.map((stat, index) => {
            const isPulsing = stat.type === 'critical' && Number(stat.value) > 0;
            const filterValue = statLabelToFilter[stat.label] || '';
            const isClickable = Boolean(onStatClick && filterValue);

            // Use button element for clickable stats to ensure proper event handling
            if (isClickable) {
              return (
                <button
                  key={index}
                  type="button"
                  className="mini-stat clickable"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onStatClick!(filterValue);
                  }}
                  title={`Filter by ${stat.label}`}
                  style={{ position: 'relative', zIndex: 10 }}
                >
                  <span
                    className={`mini-stat-dot${isPulsing ? ' pulse' : ''}`}
                    style={{ backgroundColor: statTypeColors[stat.type || 'info'] || statTypeColors.info }}
                  />
                  <span className="mini-stat-value">{stat.value}</span>
                </button>
              );
            }

            return (
              <span
                key={index}
                className="mini-stat"
                title={stat.label}
              >
                <span
                  className={`mini-stat-dot${isPulsing ? ' pulse' : ''}`}
                  style={{ backgroundColor: statTypeColors[stat.type || 'info'] || statTypeColors.info }}
                />
                <span className="mini-stat-value">{stat.value}</span>
              </span>
            );
          })}
        </div>
      )}
      {showNotConfigured && (
        <div className="mini-widget-not-configured">Not Configured</div>
      )}
    </div>
  );
}
